/**
 * @fileoverview Restore Client mutation hook.
 * @description Restores an archived client (POST /sales/clients/{tenantId}/restore) through
 * `useIdempotentMutation`, whose kept `Idempotency-Key` makes a retry after an unknown outcome a
 * replay. The command takes no `If-Match`: the backend's state refusal decides.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { restoreClient } from '@/features/sales/services/sales-clients.service'

import type { ClientRestored } from '@/features/sales/types/sales-client.types'

/**
 * @description Restores a client, then refreshes every sales query so the grid and tiles pick it up.
 * @param {(result: ClientRestored) => void} [onSuccess] - Called after a success.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the client's id).
 */
export function useRestoreClient(onSuccess?: (result: ClientRestored) => void) {
  return useIdempotentMutation<string, ClientRestored>({
    action: 'restore-client',
    /**
     * @description Sends the restore under the key it is given.
     * @param {string} tenantId - The client.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<ClientRestored>} The client's status.
     */
    send: (tenantId, key) => restoreClient(tenantId, key),
    onSuccess,
  })
}
